import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faCheck,
  faWrench,
  faTimes,
  faClock,
  faMap,
  faRuler,
  faFile,
  faCogs,
  faChartBar,
  faList,
  faRefresh,
  faExclamationTriangle,
  faHeartbeat,
  faServer,
  faBolt,
  faShieldAlt,
  faLayerGroup,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import LoadingScreen from './LoadingScreen';

const WebServices = () => {
  const [activeTab, setActiveTab] = useState('layers');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [webServicesData, setWebServicesData] = useState({ geoServerLayers: [], serviceStats: {}, wmsCapabilities: {}, wfsCapabilities: {} });
  const [geoserverLayers, setGeoserverLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load web services and GeoServer layers data from API
  useEffect(() => {
    const loadWebServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const [webServicesResponse, geoLayersResponse] = await Promise.all([
          apiService.getWebServices(),
          apiService.getGeoServerLayers()
        ]);
        setWebServicesData(webServicesResponse);
        setGeoserverLayers(geoLayersResponse);

        // Random loading time between 1-2.5 seconds
        const loadingTime = 1000 + Math.random() * 1500;
        setTimeout(() => {
          setLoading(false);
        }, loadingTime);
      } catch (err) {
        console.error('Failed to load web services:', err);
        setError('Failed to load web services data from API');
        setLoading(false);
      }
    };

    loadWebServices();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return faCheck;
      case 'maintenance': return faWrench;
      case 'error': return faTimes;
      default: return faClock;
    }
  };

  const getLayerTypeIcon = (type) => {
    switch (type) {
      case 'coverage': return faMap;
      case 'vector': return faRuler;
      default: return faFile;
    }
  };

  const toggleRow = (layerId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(layerId)) {
      newExpanded.delete(layerId);
    } else {
      newExpanded.add(layerId);
    }
    setExpandedRows(newExpanded);
  };

  const getServiceBadges = (serviceTypes) => {
    return serviceTypes.map((service, index) => (
      <span key={index} className={`service-badge ${service.toLowerCase()}`}>
        {service}
      </span>
    ));
  };

  const getErrorRateClass = (errorRate) => {
    const rate = parseFloat(errorRate);
    if (rate > 5) return 'error';
    if (rate > 1) return 'warning';
    return 'success';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="web-services-container">
        <LoadingScreen message="Loading web services..." />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="web-services-container">
        <div className="web-services-header">
          <h2>
            <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.5rem' }} />
            Web Services
          </h2>
        </div>
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="web-services-container">
      <div className="web-services-header">
        <h2>
          <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '0.5rem' }} />
          GeoServer OGC Layers
        </h2>
        <div className="services-stats">
          <div className="stat-item">
            <span className="stat-value">{geoserverLayers.length}</span>
            <span className="stat-label">Total Layers</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ color: '#10b981' }}>4</span>
            <span className="stat-label">Raw EO Data</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ color: '#3b82f6' }}>3</span>
            <span className="stat-label">Derived Products</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ color: '#f59e0b' }}>
              {geoserverLayers.reduce((sum, l) => sum + l.views, 0)}
            </span>
            <span className="stat-label">Total Views (24h)</span>
          </div>
        </div>

        <div className="service-tabs">
          <button
            className={`tab-btn ${activeTab === 'layers' ? 'active' : ''}`}
            onClick={() => setActiveTab('layers')}
          >
            <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '0.25rem' }} />
            OGC Layers ({geoserverLayers.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.25rem' }} />
            Performance Metrics
          </button>
        </div>
      </div>

      {activeTab === 'layers' && (
        <div className="data-table" style={{ maxWidth: '100%', width: '100%' }}>
          <div className="table-header">
            <h3 className="table-title">OGC Web Services Layers</h3>
            <span className="table-subtitle">Published GeoServer layers via WMS, WCS, and WFS protocols</span>
          </div>
          <div className="table-content" style={{ overflowX: 'auto' }}>
            <table className="data-table-element" style={{ width: '100%', minWidth: '1200px' }}>
              <thead>
                <tr>
                  <th style={{ width: '20%', padding: '1rem', textAlign: 'left' }}>Layer Name</th>
                  <th style={{ width: '10%', padding: '1rem', textAlign: 'left' }}>Workspace</th>
                  <th style={{ width: '8%', padding: '1rem', textAlign: 'left' }}>Type</th>
                  <th style={{ width: '12%', padding: '1rem', textAlign: 'left' }}>Services</th>
                  <th style={{ width: '10%', padding: '1rem', textAlign: 'left' }}>Views (24h)</th>
                  <th style={{ width: '12%', padding: '1rem', textAlign: 'left' }}>Avg Response</th>
                  <th style={{ width: '8%', padding: '1rem', textAlign: 'left' }}>Error Rate</th>
                  <th style={{ width: '10%', padding: '1rem', textAlign: 'left' }}>SRS</th>
                  <th style={{ width: '10%', padding: '1rem', textAlign: 'left' }}>Last Update</th>
                  <th style={{ width: '5%', padding: '1rem', textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {geoserverLayers.map((layer) => (
                  <React.Fragment key={layer.id}>
                    <tr
                      onClick={() => toggleRow(layer.id)}
                      style={{
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <FontAwesomeIcon icon={getLayerTypeIcon(layer.type)} style={{color: '#3b82f6', fontSize: '1.1rem'}} />
                          <span style={{fontWeight: '600'}}>{layer.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}><code style={{fontSize: '0.85rem', color: '#f59e0b'}}>{layer.workspace}</code></td>
                      <td style={{ padding: '1rem' }}>
                        <span className={`status-badge ${layer.type}`}>{layer.type}</span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{display: 'flex', gap: '0.25rem'}}>
                          {layer.services.map((service, idx) => (
                            <span key={idx} className={`service-badge ${service.toLowerCase()}`}>
                              {service}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}><span style={{fontWeight: '600', color: '#10b981'}}>{layer.views}</span></td>
                      <td style={{ padding: '1rem' }}>
                        <span className={layer.avgResponseTime > 200 ? 'text-warning' : 'text-success'}>
                          {layer.avgResponseTime}ms
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span className={`error-rate ${getErrorRateClass(layer.errorRate)}`}>
                          {layer.errorRate}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}><code style={{fontSize: '0.8rem'}}>{layer.srs}</code></td>
                      <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#888' }}>{layer.lastUpdate}</td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <FontAwesomeIcon
                          icon={expandedRows.has(layer.id) ? faChevronDown : faChevronRight}
                          style={{ color: '#8b5cf6', fontSize: '0.9rem' }}
                        />
                      </td>
                    </tr>
                    {expandedRows.has(layer.id) && (
                      <tr className="expanded-row">
                        <td colSpan="10">
                          <div className="expanded-content" style={{padding: '1.5rem', background: 'rgba(15, 23, 42, 0.8)'}}>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
                              <div>
                                <h4 style={{marginBottom: '1rem', color: '#e2e8f0'}}>Layer Details</h4>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>Format:</span>
                                    <span style={{marginLeft: '1rem', color: '#e2e8f0'}}>{layer.format}</span>
                                  </div>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>Bounding Box:</span>
                                    <code style={{marginLeft: '1rem', fontSize: '0.85rem', color: '#f59e0b'}}>{layer.bbox}</code>
                                  </div>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>Workspace:</span>
                                    <code style={{marginLeft: '1rem', color: '#3b82f6'}}>{layer.workspace}</code>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <h4 style={{marginBottom: '1rem', color: '#e2e8f0'}}>Performance Metrics</h4>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>24h Views:</span>
                                    <span style={{marginLeft: '1rem', fontWeight: '600', color: '#10b981'}}>{layer.views}</span>
                                  </div>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>Avg Response Time:</span>
                                    <span style={{marginLeft: '1rem', fontWeight: '600'}}>{layer.avgResponseTime}ms</span>
                                  </div>
                                  <div>
                                    <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>Error Rate:</span>
                                    <span style={{marginLeft: '1rem', fontWeight: '600', color: parseFloat(layer.errorRate) > 2 ? '#ef4444' : '#10b981'}}>{layer.errorRate}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div style={{marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                              <div style={{display: 'flex', gap: '1rem'}}>
                                <button className="action-btn" style={{padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px', color: '#3b82f6', fontSize: '0.85rem', cursor: 'pointer'}}>
                                  <FontAwesomeIcon icon={faMap} style={{marginRight: '0.5rem'}} />
                                  Preview Layer
                                </button>
                                <button className="action-btn" style={{padding: '0.5rem 1rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px', color: '#10b981', fontSize: '0.85rem', cursor: 'pointer'}}>
                                  <FontAwesomeIcon icon={faCogs} style={{marginRight: '0.5rem'}} />
                                  View Capabilities
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Performance Metrics</h3>
            <span className="table-subtitle">Layer performance analytics and response times</span>
          </div>
          <div className="table-content">
            <div className="health-overview-grid">
              {/* Service Status Card */}
              <div className="health-card service-status">
                <div className="health-card-header">
                  <FontAwesomeIcon icon={faServer} />
                  <h4>Service Status</h4>
                  <span className={`health-indicator ${webServicesData.serviceStats.overallHealth || 'healthy'}`}></span>
                </div>
                <div className="health-metrics">
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.uptime || '99.8%'}</span>
                    <span className="metric-label">Uptime (30d)</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.online || 0}</span>
                    <span className="metric-label">Healthy Layers</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.error || 0}</span>
                    <span className="metric-label">Failed Layers</span>
                  </div>
                </div>
              </div>

              {/* Performance Summary Card */}
              <div className="health-card performance-summary">
                <div className="health-card-header">
                  <FontAwesomeIcon icon={faBolt} />
                  <h4>Performance Summary</h4>
                  <span className={`health-indicator ${parseFloat(webServicesData.serviceStats.overallErrorRate || '0%') < 2 ? 'healthy' : 'warning'}`}></span>
                </div>
                <div className="health-metrics">
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.avgResponseTime || '150ms'}</span>
                    <span className="metric-label">Avg Response</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.overallErrorRate || '0.5%'}</span>
                    <span className="metric-label">Error Rate</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">{webServicesData.serviceStats.cacheHitRate || '87%'}</span>
                    <span className="metric-label">Cache Hits</span>
                  </div>
                </div>
              </div>

              {/* Security & Compliance Card */}
              <div className="health-card security-summary">
                <div className="health-card-header">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <h4>Security & Compliance</h4>
                  <span className="health-indicator healthy"></span>
                </div>
                <div className="health-metrics">
                  <div className="health-metric">
                    <span className="metric-value">SSL</span>
                    <span className="metric-label">Encryption</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">Active</span>
                    <span className="metric-label">Auth</span>
                  </div>
                  <div className="health-metric">
                    <span className="metric-value">0</span>
                    <span className="metric-label">Vulnerabilities</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Critical Issues Section */}
            {webServicesData.geoServerLayers?.filter(layer => layer.status === 'error').length > 0 && (
              <div className="critical-issues-section">
                <h4>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: '#ef4444' }} />
                  Critical Issues Requiring Attention
                </h4>
                <div className="critical-issues-list">
                  {webServicesData.geoServerLayers
                    .filter(layer => layer.status === 'error')
                    .slice(0, 5)
                    .map(layer => (
                      <div key={layer.id} className="critical-issue-item">
                        <div className="issue-header">
                          <FontAwesomeIcon icon={faTimes} className="issue-icon" />
                          <span className="layer-name">{layer.workspace}:{layer.name}</span>
                          <span className="issue-time">Error since {layer.lastUpdated}</span>
                        </div>
                        <div className="issue-description">
                          {layer.error || 'Layer is not responding to requests'}
                        </div>
                        <div className="issue-actions">
                          <button className="action-btn diagnose">
                            <FontAwesomeIcon icon={faCogs} style={{ marginRight: '0.25rem' }} />
                            Diagnose
                          </button>
                          <button className="action-btn restart">
                            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.25rem' }} />
                            Restart
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Health Trends Visualization */}
            <div className="health-trends-section">
              <h4>
                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                Health Trends (Last 7 Days)
              </h4>
              <div className="trends-grid">
                <div className="trend-item">
                  <span className="trend-label">Response Time</span>
                  <div className="sparkline-container">
                    <svg className="sparkline" viewBox="0 0 100 30">
                      <polyline
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        points="0,15 15,12 30,8 45,10 60,6 75,9 90,7 100,5"
                      />
                    </svg>
                    <span className="trend-value improving">↓ 15ms</span>
                  </div>
                </div>
                <div className="trend-item">
                  <span className="trend-label">Error Rate</span>
                  <div className="sparkline-container">
                    <svg className="sparkline" viewBox="0 0 100 30">
                      <polyline
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="2"
                        points="0,20 15,18 30,22 45,19 60,15 75,12 90,10 100,8"
                      />
                    </svg>
                    <span className="trend-value improving">↓ 0.2%</span>
                  </div>
                </div>
                <div className="trend-item">
                  <span className="trend-label">Request Volume</span>
                  <div className="sparkline-container">
                    <svg className="sparkline" viewBox="0 0 100 30">
                      <polyline
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        points="0,25 15,20 30,18 45,22 60,16 75,14 90,12 100,10"
                      />
                    </svg>
                    <span className="trend-value stable">↑ 12%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'layers' && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">GeoServer Layer Management ({webServicesData.geoServerLayers.length} layers)</h3>
            <span className="table-subtitle">Click rows to view layer details, metadata, and service endpoints</span>
          </div>
          <div className="table-content">
            <table className="log-table">
              <thead>
                <tr>
                  <th style={{ width: '250px' }}>Layer Name</th>
                  <th style={{ width: '120px' }}>Type</th>
                  <th style={{ width: '150px' }}>Services</th>
                  <th style={{ width: '100px' }}>Status</th>
                  <th style={{ width: '120px' }}>Requests/24h</th>
                  <th style={{ width: '100px' }}>Error Rate</th>
                </tr>
              </thead>
              <tbody>
                {webServicesData.geoServerLayers.map(layer => (
                  <React.Fragment key={layer.id}>
                    <tr
                      className={`expandable ${expandedRows.has(layer.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRow(layer.id)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={getLayerTypeIcon(layer.type)} />
                          <div>
                            <div style={{ fontWeight: 500 }}>{layer.workspace}:{layer.name}</div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{layer.metadata?.title}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: '#94a3b8', textTransform: 'capitalize' }}>{layer.type}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {getServiceBadges(layer.serviceTypes)}
                        </div>
                      </td>
                      <td>
                        <span className={`severity-badge ${layer.status}`}>
                          <FontAwesomeIcon icon={getStatusIcon(layer.status)} style={{ marginRight: '0.25rem' }} />
                          {layer.status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', color: '#e2e8f0' }}>
                        {layer.requests24h.toLocaleString()}
                      </td>
                      <td>
                        <span className={`severity-badge ${getErrorRateClass(layer.errorRate)}`}>
                          {layer.errorRate}
                        </span>
                      </td>
                    </tr>
                    {expandedRows.has(layer.id) && (
                      <tr className="expandable-row">
                        <td colSpan="6">
                          <div className="expandable-content">
                            <div className="layer-details-grid">
                              <div className="metadata-section">
                                <h4>
                                  <FontAwesomeIcon icon={faList} style={{ marginRight: '0.5rem' }} />
                                  Layer Metadata
                                </h4>
                                <div className="properties-grid">
                                  <div className="property-item">
                                    <span className="prop-label">Title:</span>
                                    <span className="prop-value">{layer.metadata?.title}</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Description:</span>
                                    <span className="prop-value">{layer.metadata?.description}</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Native CRS:</span>
                                    <span className="prop-value" style={{ fontFamily: 'monospace' }}>
                                      {layer.bbox?.crs || 'EPSG:4326'}
                                      <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                                        ({layer.bbox?.crs === 'EPSG:4326' ? 'WGS84 Geographic' :
                                          layer.bbox?.crs === 'EPSG:3857' ? 'Web Mercator' :
                                          layer.bbox?.crs?.includes('326') ? 'WGS84 / UTM Zone ' + layer.bbox.crs.slice(-2) + 'N' :
                                          'Projected'})
                                      </span>
                                    </span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Keywords:</span>
                                    <span className="prop-value">{layer.metadata?.keywords?.join(', ')}</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Last Updated:</span>
                                    <span className="prop-value">{layer.lastUpdated}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="bbox-section">
                                <h4>
                                  <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.5rem' }} />
                                  Bounding Box
                                </h4>
                                <div className="properties-grid">
                                  <div className="property-item">
                                    <span className="prop-label">Min X:</span>
                                    <span className="prop-value">{layer.bbox?.minx}°</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Min Y:</span>
                                    <span className="prop-value">{layer.bbox?.miny}°</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Max X:</span>
                                    <span className="prop-value">{layer.bbox?.maxx}°</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">Max Y:</span>
                                    <span className="prop-value">{layer.bbox?.maxy}°</span>
                                  </div>
                                  <div className="property-item">
                                    <span className="prop-label">CRS:</span>
                                    <span className="prop-value" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                      {layer.bbox?.crs || 'EPSG:4326'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Band Information for Raster Layers */}
                            {layer.type === 'coverage' && (
                              <div className="bands-section">
                                <h4>
                                  <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '0.5rem' }} />
                                  Band Information
                                </h4>
                                <div className="bands-grid">
                                  {(layer.bands || [
                                    { number: 1, name: 'B02 - Blue', wavelength: '490nm', resolution: '10m', bitDepth: 'UInt16' },
                                    { number: 2, name: 'B03 - Green', wavelength: '560nm', resolution: '10m', bitDepth: 'UInt16' },
                                    { number: 3, name: 'B04 - Red', wavelength: '665nm', resolution: '10m', bitDepth: 'UInt16' },
                                    { number: 4, name: 'B08 - NIR', wavelength: '842nm', resolution: '10m', bitDepth: 'UInt16' }
                                  ]).map((band, idx) => (
                                    <div key={idx} className="band-item">
                                      <span className="band-number">Band {band.number}</span>
                                      <div className="band-details">
                                        <div className="band-name">{band.name}</div>
                                        <div className="band-specs">
                                          <span className="band-spec">λ: {band.wavelength}</span>
                                          <span className="band-spec">Res: {band.resolution}</span>
                                          <span className="band-spec">{band.bitDepth}</span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="band-summary" style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '6px', fontSize: '0.85rem', color: '#94a3b8' }}>
                                  <strong>Total Bands:</strong> {(layer.bands || []).length || 4} |
                                  <strong style={{ marginLeft: '1rem' }}>Data Type:</strong> {layer.dataType || 'UInt16'} |
                                  <strong style={{ marginLeft: '1rem' }}>No Data Value:</strong> {layer.noDataValue || '0'}
                                </div>
                              </div>
                            )}

                            <div className="performance-section">
                              <h4>
                                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                                Performance Metrics
                              </h4>
                              <div className="metrics-grid">
                                <div className="metric-item">
                                  <span className="metric-label">Average Response Time:</span>
                                  <span className="metric-value">{layer.avgResponseTime}</span>
                                </div>
                                <div className="metric-item">
                                  <span className="metric-label">Requests (24h):</span>
                                  <span className="metric-value">{layer.requests24h.toLocaleString()}</span>
                                </div>
                                <div className="metric-item">
                                  <span className="metric-label">Error Rate:</span>
                                  <span className={`metric-value ${getErrorRateClass(layer.errorRate)}`}>
                                    {layer.errorRate}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="endpoints-section">
                              <h4>
                                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.5rem' }} />
                                Service Endpoints
                              </h4>
                              <div className="endpoints-list">
                                {layer.serviceTypes.map(service => (
                                  <div key={service} className="endpoint-item">
                                    <span className={`service-badge ${service.toLowerCase()}`}>{service}</span>
                                    <code className="endpoint-url">
                                      /geoserver/{layer.workspace}/{service.toLowerCase()}
                                    </code>
                                    <button className="test-endpoint-btn">Test</button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {layer.error && (
                              <div className="ai-suggestion">
                                <div className="ai-suggestion-header">
                                  <FontAwesomeIcon icon={faTimes} />
                                  <strong>Layer Issue Detected</strong>
                                  <span className="confidence-badge">Action Required</span>
                                </div>
                                <div className="ai-suggestion-content">
                                  <p><strong>Issue:</strong> {layer.error}</p>
                                  <p><strong>Recommendation:</strong> Check layer configuration and data source connectivity.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'capabilities' && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">OGC Service Capabilities</h3>
            <span className="table-subtitle">WMS and WFS service specifications and limits</span>
          </div>
          <div className="table-content">
            <div className="capabilities-grid">
              <div className="capability-card">
                <div className="capability-header">
                  <h3>
                    <FontAwesomeIcon icon={faMap} style={{ marginRight: '0.5rem' }} />
                    WMS (Web Map Service)
                  </h3>
                  <span className="version-badge">Version {webServicesData.wmsCapabilities.version}</span>
                </div>
                <div className="capability-details">
                  <div className="detail-item">
                    <span className="detail-label">Max Width:</span>
                    <span className="detail-value">{webServicesData.wmsCapabilities.maxWidth}px</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Max Height:</span>
                    <span className="detail-value">{webServicesData.wmsCapabilities.maxHeight}px</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Supported Formats:</span>
                    <div className="formats-list">
                      {webServicesData.wmsCapabilities.supportedFormats?.map(format => (
                        <span key={format} className="format-badge">{format}</span>
                      ))}
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Coordinate Systems:</span>
                    <div className="crs-list">
                      {webServicesData.wmsCapabilities.supportedProjections?.map(crs => (
                        <span key={crs} className="crs-badge">{crs}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="capability-card">
                <div className="capability-header">
                  <h3>
                    <FontAwesomeIcon icon={faRuler} style={{ marginRight: '0.5rem' }} />
                    WFS (Web Feature Service)
                  </h3>
                  <span className="version-badge">Version {webServicesData.wfsCapabilities.version}</span>
                </div>
                <div className="capability-details">
                  <div className="detail-item">
                    <span className="detail-label">Max Features:</span>
                    <span className="detail-value">{webServicesData.wfsCapabilities.maxFeatures?.toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Supported Formats:</span>
                    <div className="formats-list">
                      {webServicesData.wfsCapabilities.supportedFormats?.map(format => (
                        <span key={format} className="format-badge">{format}</span>
                      ))}
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Operations:</span>
                    <div className="operations-list">
                      {webServicesData.wfsCapabilities.operations?.map(op => (
                        <span key={op} className="operation-badge">{op}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Service Performance Statistics</h3>
            <span className="table-subtitle">Overall GeoServer performance metrics</span>
          </div>
          <div className="table-content">
            <div className="performance-overview">
              <div className="perf-card">
                <h4>
                  <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                  Overall Statistics
                </h4>
                <div className="perf-metrics">
                  <div className="metric">
                    <span className="metric-value">{webServicesData.serviceStats.totalRequests24h.toLocaleString()}</span>
                    <span className="metric-label">Total Requests (24h)</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{webServicesData.serviceStats.avgResponseTime}</span>
                    <span className="metric-label">Avg Response Time</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{webServicesData.serviceStats.overallErrorRate}</span>
                    <span className="metric-label">Overall Error Rate</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{webServicesData.serviceStats.cacheHitRate}</span>
                    <span className="metric-label">Cache Hit Rate</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{webServicesData.serviceStats.diskUsage}</span>
                    <span className="metric-label">Disk Usage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebServices;